import { parseISO } from "date-fns";
import { z } from "zod";

export const FormNameSchema = z.string().trim().min(1, "Enter a form name.")
  .refine(name => Array.from(name).length <= 100, "Use 100 characters or fewer.");

const FormSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  versionId: z.string(),
  version: z.number().int().positive(),
  name: z.string(),
  definition: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  createdBy: z.string(),
  modifiedBy: z.string(),
  versionCreatedAt: z.date(),
  versionCreatedBy: z.string(),
});

export const CreateFormRequestSchema = z.object({
  name: FormNameSchema,
  definition: z.string(),
});
export const UpdateFormRequestSchema = CreateFormRequestSchema.extend({
  expectedVersion: z.number().int().positive().max(2147483647),
});

const FormDtoSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  version_id: z.string(),
  version: z.number().int().positive(),
  name: z.string(),
  definition: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  created_by: z.string(),
  modified_by: z.string(),
  version_created_at: z.string(),
  version_created_by: z.string(),
});
export const CreateFormDtoSchema = z.object({ name: FormNameSchema, definition: z.string() });
export const UpdateFormDtoSchema = CreateFormDtoSchema.extend({
  expected_version: z.number().int().positive().max(2147483647),
});

export type FormDto = z.infer<typeof FormDtoSchema>;
export type CreateFormDto = z.infer<typeof CreateFormDtoSchema>;
export type UpdateFormDto = z.infer<typeof UpdateFormDtoSchema>;
export type Form = z.infer<typeof FormSchema>;
export type CreateFormRequest = z.infer<typeof CreateFormRequestSchema>;
export type UpdateFormRequest = z.infer<typeof UpdateFormRequestSchema>;

export function fromFormDtoToForm(dto: FormDto): Form {
  const formDto = FormDtoSchema.parse(dto);
  return FormSchema.parse({
    id: formDto.id,
    businessId: formDto.business_id,
    versionId: formDto.version_id,
    version: formDto.version,
    name: formDto.name,
    definition: formDto.definition,
    createdAt: parseISO(formDto.created_at),
    modifiedAt: parseISO(formDto.modified_at),
    createdBy: formDto.created_by,
    modifiedBy: formDto.modified_by,
    versionCreatedAt: parseISO(formDto.version_created_at),
    versionCreatedBy: formDto.version_created_by,
  });
}

export function fromCreateFormRequestToCreateFormDto(request: CreateFormRequest): CreateFormDto {
  const data = CreateFormRequestSchema.parse(request);
  return { name: data.name, definition: data.definition };
}

export function fromUpdateFormRequestToUpdateFormDto(request: UpdateFormRequest): UpdateFormDto {
  const data = UpdateFormRequestSchema.parse(request);
  return { name: data.name, definition: data.definition, expected_version: data.expectedVersion };
}
